import React, { useEffect, useState } from 'react';

import {
  DefinedRatioMetric,
  getIqmaxHealth,
  MetricValue,
  ProductHealthDashboard,
} from '../api/dashboards';
import { DefinitionTag } from '../components/MetricDefinition';
import { PLATFORM_CURRENCY, formatMoney } from '../../../commodity/format/money';

const pct = (v: number) => `${(v * 100).toFixed(1)}%`;
const fmtIso = (iso: string) => new Date(iso).toLocaleString('es-MX');

const Tile: React.FC<{
  label: string;
  value: string;
  sub?: string;
  color?: string;
  children?: React.ReactNode;
}> = ({ label, value, sub, color = '#222', children }) => (
  <div
    style={{
      border: '1px solid #d1d5da',
      borderRadius: 8,
      padding: 16,
      flex: '1 1 200px',
      minWidth: 200,
      background: '#fff',
    }}
  >
    <div style={{ fontSize: 12, color: '#666' }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>{sub}</div>}
    {children}
  </div>
);

const MetricTile: React.FC<{ label: string; metric: MetricValue }> = ({ label, metric }) => (
  <Tile
    label={label}
    value={metric.status === 'N_A' || metric.value === null ? 'N/A' : pct(metric.value)}
    sub={`${metric.numerator} / ${metric.denominator} · rolling ${metric.rollingWindow}`}
  />
);

/**
 * ADM-13-FR-02..04 — the A03 class-health ratio over the dashboard's window,
 * with the definition it was computed under. 0/0 is "N/A", never 0 %.
 */
const DefinedTile: React.FC<{
  label: string;
  metric: DefinedRatioMetric;
  notApplicable?: number;
}> = ({ label, metric, notApplicable }) => (
  <Tile
    label={label}
    value={metric.status === 'N_A' || metric.value === null ? 'N/A' : pct(metric.value)}
    sub={`${metric.numerator} / ${metric.denominator}${
      notApplicable ? ` · ${notApplicable} no aplican` : ''
    }`}
  >
    <div style={{ marginTop: 4 }}>
      <DefinitionTag stamp={metric.definition} />
    </div>
  </Tile>
);

/**
 * PI-3 Epic 4 S4.1 — IQMax product-health dashboard.
 * NSM-aligned tiles. Mobile-first: tiles wrap into single column at < 768px.
 */
const AdminProductHealthDashboard: React.FC = () => {
  const [data, setData] = useState<ProductHealthDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getIqmaxHealth();
        if (!cancelled) setData(res);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'No se pudo cargar el dashboard');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <p style={{ color: '#c92a2a' }}>{error}</p>;
  if (!data) return <p>Cargando dashboard de salud...</p>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 4px' }}>
      <h1 style={{ marginBottom: 4 }}>Salud del producto IQMax</h1>
      <p style={{ color: '#666', marginTop: 0, fontSize: 12 }}>
        Generado: {fmtIso(data.generatedAt)}
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
        <DefinedTile label="Ciclos completos" metric={data.cycleCompletionRate} />
        <DefinedTile label="Asistencia" metric={data.attendanceRate} />
        <DefinedTile
          label="Rúbricas"
          metric={data.rubricCompletionRate}
          notApplicable={data.rubricCompletionRate.notApplicable}
        />
        <MetricTile label="Dispute rate" metric={data.disputeRate} />
      </div>

      <h2 style={{ marginTop: 24 }}>
        Holdback distribution ({data.holdbackDistribution.rollingWindow})
      </h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <Tile label="Pendiente" value={String(data.holdbackDistribution.pending)} color="#1971c2" />
        <Tile label="Liberado" value={String(data.holdbackDistribution.released)} color="#2b8a3e" />
        <Tile
          label="Reembolsado"
          value={String(data.holdbackDistribution.refunded)}
          color="#c92a2a"
        />
      </div>

      <h2 style={{ marginTop: 24 }}>Release accuracy</h2>
      {data.releaseAccuracy ? (
        <div
          style={{
            padding: 14,
            border: '1px solid #d1d5da',
            borderRadius: 8,
            background: '#fff',
            fontSize: 13,
          }}
        >
          <div
            style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}
          >
            <strong style={{ fontSize: 18, color: '#2b8a3e' }}>
              {pct(data.releaseAccuracy.accuracy)}
            </strong>
            <span style={{ color: '#666' }}>Run: {fmtIso(data.releaseAccuracy.completedAt)}</span>
          </div>
          <div style={{ marginTop: 8 }}>
            Expected {formatMoney(data.releaseAccuracy.expectedTotal, PLATFORM_CURRENCY)} · Observed{' '}
            {formatMoney(data.releaseAccuracy.observedTotal, PLATFORM_CURRENCY)} · Mismatches{' '}
            {data.releaseAccuracy.mismatches}
          </div>
          <div style={{ marginTop: 4, fontSize: 11, color: '#888', fontFamily: 'monospace' }}>
            runId: {data.releaseAccuracy.runId}
          </div>
        </div>
      ) : (
        <p style={{ color: '#666' }}>
          Aún no se ha ejecutado un pase de reconciliación. Corre{' '}
          <code>POST /admin/reconciliation/release/run</code>.
        </p>
      )}
    </div>
  );
};

export default AdminProductHealthDashboard;
